/**
 * COMPLETED-ORDER-DOC-UPLOAD UI — premium "Add Document to Completed Order" modal.
 * Presentation only: validation (FILE_CATEGORIES), upload call, permissions and audit live in FilesTab.handleCategoryUpload.
 */
import React, { useEffect, useRef, useState } from 'react';
import { FileText, NotebookPen, Image as ImageIcon, Check, X, CloudUpload, Sparkles, Loader2, Upload, Info, Trash2, File as FileIcon } from 'lucide-react';
import { FILE_CATEGORIES, validateFileForCategory } from './FileUploadModal';
import './post-completion-upload.css';

const CATS = [
  { id: 'supporting_document', label: 'Supporting Document', desc: 'Client/Order related documents', Icon: FileText, bg: '#eff6ff', c: '#2563eb' },
  { id: 'internal_note', label: 'Internal Note', desc: 'Internal notes or communications', Icon: NotebookPen, bg: '#fff7ed', c: '#ea580c' },
  { id: 'image', label: 'Image / Screenshot', desc: 'Screenshots or images', Icon: ImageIcon, bg: '#f0f9ff', c: '#0284c7' },
];
const MIN_NOTE = 5;
const MAX_NOTE = 500;
const fmtSize = (b) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
const supportsText = (catId) => {
  const c = FILE_CATEGORIES[catId];
  if (!c) return '';
  return `Supports ${c.allowedExtensions.map(e => e.replace('.', '').toUpperCase()).join(', ')} (Max ${Math.round(c.maxSize / 1024 / 1024)} MB each)`;
};

export const PostCompletionUploadModal = ({ isOpen, onClose, onUpload, uploading }) => {
  const [closing, setClosing] = useState(false);
  const [category, setCategory] = useState('');
  const [items, setItems] = useState([]); // { file, error }
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (isOpen) { setClosing(false); setCategory(''); setItems([]); setNote(''); setNoteTouched(false); setDragActive(false); submittingRef.current = false; }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape' && !uploading) requestClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const requestClose = () => {
    if (uploading) return;
    setClosing(true);
    setTimeout(onClose, 150);
  };

  const addFiles = (list) => {
    if (!category) return;
    const next = Array.from(list || []).map(file => ({ file, error: validateFileForCategory(file, category).error || null }));
    setItems(prev => [...prev, ...next.filter(n => !prev.some(p => p.file.name === n.file.name && p.file.size === n.file.size))]);
  };

  const pickCategory = (id) => {
    setCategory(id);
    setItems(prev => prev.map(it => ({ ...it, error: validateFileForCategory(it.file, id).error || null })));
  };

  const validFiles = items.filter(i => !i.error).map(i => i.file);
  const noteOk = note.trim().length >= MIN_NOTE;
  const canSubmit = !!category && validFiles.length > 0 && items.every(i => !i.error) && noteOk && !uploading;

  const submit = () => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    Promise.resolve(onUpload(validFiles, category, null, note.trim())).finally(() => { submittingRef.current = false; });
  };

  if (!isOpen) return null;
  const cat = CATS.find(c => c.id === category);

  return (
    <div className="pc-overlay" data-closing={closing} onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }} data-testid="post-completion-upload-modal">
      <div className="pc-modal" role="dialog" aria-modal="true" aria-labelledby="pc-title">
        <div className="pc-modal-head">
          <span className="pc-icon-tile" style={{ width: 52, height: 52 }}><FileText className="w-6 h-6" /></span>
          <div className="flex-1 min-w-0">
            <h2 id="pc-title" className="text-xl font-bold text-slate-900 leading-tight">Add Document to Completed Order</h2>
            <p className="text-sm text-slate-500 mt-1">Reference only — the order stays Completed and nothing is delivered or resent.</p>
          </div>
          <button type="button" className="pc-close" onClick={requestClose} disabled={uploading} aria-label="Close" data-testid="post-completion-close-btn"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <section>
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-sm font-semibold text-slate-800">File Category <span className="text-rose-600">*</span></p>
              {!category && <p className="flex items-center gap-1.5 text-xs text-slate-500"><Info className="w-3.5 h-3.5" /> Select a category to continue</p>}
            </div>
            <div className="pc-cats" role="group" aria-label="File category">
              {CATS.map(c => (
                <button key={c.id} type="button" className="pc-cat" aria-pressed={category === c.id} disabled={uploading} onClick={() => pickCategory(c.id)} data-testid={`category-${c.id}`}>
                  {category === c.id && <span className="pc-check" data-testid={`category-check-${c.id}`}><Check className="w-3.5 h-3.5" strokeWidth={3} /></span>}
                  <span className="pc-cat-icon" style={{ background: c.bg, color: c.c }}><c.Icon className="w-6 h-6" /></span>
                  <span className="pc-cat-label">{c.label}</span>
                  <span className="pc-cat-desc">{c.desc}</span>
                </button>
              ))}
            </div>
          </section>

          <section>
            <div className="pc-drop" data-active={dragActive} data-disabled={!category}
              onClick={() => category && !uploading && inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); if (category) setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => { e.preventDefault(); setDragActive(false); addFiles(e.dataTransfer.files); }}
              data-testid="post-completion-dropzone">
              <Sparkles className="pc-spark w-4 h-4" style={{ left: '42%', top: 28 }} />
              <Sparkles className="pc-spark w-5 h-5" style={{ right: '40%', top: 20 }} />
              <CloudUpload className="pc-cloud" strokeWidth={1.6} />
              <p className="text-base font-semibold text-slate-900">Drag &amp; drop files here</p>
              <p className="text-sm font-semibold text-violet-700 mt-0.5">or click to browse</p>
              <p className="text-xs text-slate-500 mt-2">{category ? supportsText(category) : 'Choose a category above to enable uploads'}</p>
              <input ref={inputRef} type="file" multiple className="hidden" accept={FILE_CATEGORIES[category]?.allowedExtensions?.join(',')}
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="post-completion-file-input" />
            </div>
            {items.length > 0 && (
              <ul className="mt-3 space-y-2" data-testid="post-completion-file-list">
                {items.map((it, i) => (
                  <li key={`${it.file.name}-${it.file.size}`} className="pc-file">
                    <span className="pc-file-icon"><FileIcon className="w-4 h-4" /></span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{it.file.name}</p>
                      <p className="text-xs text-slate-500">{fmtSize(it.file.size)}{it.error ? ` · ${it.error}` : cat ? ` · ${cat.label}` : ''}</p>
                      {uploading && !it.error && <div className="pc-progress"><span /></div>}
                    </div>
                    <span className="pc-status" data-s={it.error ? 'invalid' : uploading ? 'uploading' : 'ready'} data-testid={`post-completion-file-status-${i}`}>
                      {it.error ? 'Not allowed' : uploading ? 'Uploading…' : 'Ready'}
                    </span>
                    {!uploading && (
                      <button type="button" onClick={() => setItems(prev => prev.filter((_, j) => j !== i))} className="p-1 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50" aria-label="Remove file" data-testid={`post-completion-file-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <label htmlFor="pc-note" className="block text-sm font-semibold text-slate-800 mb-2">Reason / Note <span className="text-rose-600">*</span></label>
            <div className="relative">
              <textarea id="pc-note" className="pc-textarea" rows={2} maxLength={MAX_NOTE} value={note} disabled={uploading}
                onChange={(e) => setNote(e.target.value)} onBlur={() => setNoteTouched(true)}
                aria-invalid={noteTouched && !noteOk} placeholder="e.g., Client requested additional mortgage document after completion."
                data-testid="post-completion-note-input" />
              <span className="absolute right-3 bottom-2 text-[11px] text-slate-400" data-testid="post-completion-note-counter">{note.length}/{MAX_NOTE}</span>
            </div>
            <p className={`mt-1.5 flex items-center gap-1.5 text-xs ${noteTouched && !noteOk ? 'text-rose-600' : 'text-slate-500'}`} data-testid="post-completion-note-hint">
              <Info className="w-3.5 h-3.5" />
              {noteTouched && !noteOk ? `Please enter at least ${MIN_NOTE} characters so the reason is recorded in the audit trail.` : 'Recorded in the order timeline with your name, role, date/time, filename and category. Minimum 5 characters.'}
            </p>
          </section>
        </div>

        <div className="pc-foot">
          <button type="button" className="pc-btn-ghost" onClick={requestClose} disabled={uploading} data-testid="post-completion-cancel-btn">Cancel</button>
          <button type="button" className="pc-grad-btn h-[42px] px-5 inline-flex items-center gap-2 text-sm" onClick={submit} disabled={!canSubmit} data-testid="upload-files-btn">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</> : <><Upload className="w-4 h-4" /> Add Document{validFiles.length > 1 ? ` (${validFiles.length})` : ''}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostCompletionUploadModal;
